import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { forwardRef, useId, useImperativeHandle, useRef, useState } from "react";
import { cn } from "@repo/utils";
import { inputVariants } from "./input-variants.js";
import { ClearButton } from "../button/components/clear-button.js";

export type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  containerClassName?: string;
} & VariantProps<typeof inputVariants> & {
    label?: string;
    error?: string;
    helperText?: string;
    showClearIcon?: boolean;
    onClear?: () => void;
  };

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      containerClassName,
      variant,
      inputSize,
      label,
      error,
      helperText,
      type,
      id,
      showClearIcon,
      onClear,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = !!error;

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const isControlled = props.value !== undefined;
    const [uncontrolledHasValue, setUncontrolledHasValue] = useState(
      props.defaultValue != null && String(props.defaultValue) !== ""
    );
    const hasValue = isControlled ? String(props.value ?? "") !== "" : uncontrolledHasValue;
    const canClear = showClearIcon && hasValue && !props.disabled && !props.readOnly;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setUncontrolledHasValue(e.target.value !== "");
      onChange?.(e);
    };

    const handleClear = () => {
      const input = inputRef.current;
      if (!input) return;

      const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
      setValue?.call(input, "");
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.focus();
      onClear?.();
    };

    return (
      <div className={cn("w-full", containerClassName)}>
        {label && (
          <label htmlFor={inputId} className="mb-2 block text-sm font-medium">
            {label}
            {props.required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            id={inputId}
            type={type}
            className={cn(
              inputVariants({ variant: hasError ? "error" : variant, inputSize }),
              showClearIcon && "pr-10",
              className
            )}
            ref={inputRef}
            aria-invalid={hasError}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            onChange={handleChange}
            {...props}
          />

          {canClear && (
            <div className="absolute inset-y-0 right-2 flex items-center">
              <ClearButton onClick={handleClear} />
            </div>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-500">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input"; // For React DevTools

export { Input };
